import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, Coffee, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import Navbar from '@/components/Navbar';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Support() {
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');
  const [stats, setStats] = useState(null);
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/support/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleDonate = async (amount) => {
    if (amount < 1) {
      toast.error('Minimum donation is €1');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(
        `${API}/support/donate`,
        {
          amount: amount,
          message: message,
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );

      window.location.href = response.data.url;
    } catch (error) {
      console.error('Failed to create donation:', error);
      toast.error('Failed to process donation');
      setProcessing(false);
    }
  };

  const presetAmounts = [3, 5, 10, 20];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-gradient-to-br from-rose-100 to-orange-100 mb-6">
            <Heart className="h-10 w-10 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-extrabold tracking-tight mb-4">
            Support Even Rounds
          </h1>
          <p className="text-lg text-muted-foreground font-body max-w-2xl mx-auto">
            Even Rounds is free for everyone. If you find it helpful and want to support continued development, you can buy us a coffee (or yarn!) ☕🧶
          </p>
        </div>

        {stats && (
          <div className="rounded-3xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 p-6 mb-8 text-center">
            <p className="text-sm text-muted-foreground mb-2">Community Support</p>
            <p className="text-3xl font-heading font-bold text-accent mb-1">
              €{stats.total_amount}
            </p>
            <p className="text-sm text-muted-foreground">
              from {stats.total_supporters} amazing {stats.total_supporters === 1 ? 'supporter' : 'supporters'}
            </p>
          </div>
        )}

        <div className="rounded-3xl bg-white border border-stone-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] mb-8">
          <h2 className="text-2xl font-heading font-bold mb-6">Choose Your Support Amount</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {presetAmounts.map((amount) => (
              <button
                key={amount}
                onClick={() => handleDonate(amount)}
                disabled={processing}
                className="relative rounded-2xl border-2 border-stone-200 p-6 text-center hover:border-primary hover:bg-rose-50 transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Coffee className="h-8 w-8 text-primary mx-auto mb-2 group-hover:animate-bounce" />
                <p className="text-2xl font-heading font-bold mb-1">€{amount}</p>
                <p className="text-xs text-muted-foreground">
                  {amount === 3 && 'Coffee'}
                  {amount === 5 && 'Latte'}
                  {amount === 10 && 'Lunch'}
                  {amount === 20 && 'Yarn!'}
                </p>
              </button>
            ))}
          </div>

          <div className="border-t border-stone-200 pt-6">
            <h3 className="text-lg font-heading font-semibold mb-4">Or Choose Custom Amount</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="amount">Amount (€)</Label>
                <Input
                  id="amount"
                  type="number"
                  min="1"
                  step="0.01"
                  placeholder="Enter amount"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label htmlFor="message">Message (Optional)</Label>
                <Textarea
                  id="message"
                  placeholder="Leave a thank you note..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="rounded-xl"
                />
              </div>
            </div>
            <div className="mt-6">
              <Button
                onClick={() => handleDonate(parseFloat(customAmount))}
                disabled={!customAmount || parseFloat(customAmount) < 1 || processing}
                className="w-full md:w-auto rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5 transition-all duration-300 font-bold"
              >
                {processing ? (
                  <>
                    <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-solid border-white border-r-transparent"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Heart className="h-4 w-4 mr-2" />
                    Support with €{customAmount || '0'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        <div className="rounded-3xl bg-white border border-stone-100 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <h2 className="text-2xl font-heading font-bold mb-4">Why Support Even Rounds?</h2>
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-heading font-semibold mb-1">Keep It Free</h3>
                <p className="text-sm text-muted-foreground">
                  Your support helps us keep the core features free for everyone in the yarn crafting community.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="h-10 w-10 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-5 w-5 text-secondary" />
              </div>
              <div>
                <h3 className="font-heading font-semibold mb-1">New Features</h3>
                <p className="text-sm text-muted-foreground">
                  Donations help fund development of new features, AI improvements, and integrations.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-4">
              <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-5 w-5 text-accent" />
              </div>
              <div>
                <h3 className="font-heading font-semibold mb-1">Server Costs</h3>
                <p className="text-sm text-muted-foreground">
                  Help cover hosting, AI scanning costs, and database infrastructure.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 p-6 rounded-2xl bg-stone-50 border border-stone-100">
            <p className="text-sm text-muted-foreground text-center">
              <strong>100% voluntary.</strong> All core features remain free forever, regardless of support.
              Your donation is a gift to help us continue improving Even Rounds for the community. ❤️
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
